import { useState, type ReactNode } from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ShieldCheck } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { useMySalarie } from "@/lib/data";
import {
  aonPrivacyConsentNeeded,
  pickLegalText,
  useAcceptAonPrivacy,
  useLegalTextCurrent,
  type LegalTextCurrent,
} from "@/lib/data";
import { Dialog } from "@/components/ui/Dialog";
import { Button } from "@/components/ui/Button";
import { LegalDocumentLinks } from "@/components/legal/LegalDocumentLinks";
import { LegalTextBody } from "@/components/legal/LegalTextBody";
import { IaMention } from "@/components/legal/IaMention";

// =============================================================================
// AonPrivacyGate — engagement de confidentialité Aon (A2).
// -----------------------------------------------------------------------------
// Monté autour de l'espace applicatif : si la fiche salarié de l'utilisateur
// connecté n'a pas accepté la version exigée du texte `aon_privacy`, l'espace
// salarié n'est pas rendu du tout (aucune requête métier n'est lancée) et une
// fenêtre non fermable impose la lecture + case à cocher.
//
// - Évolutif : `required_version` ne tient compte que des versions marquées
//   « à réaccepter » ; une « correction mineure » ne redéclenche rien.
// - Usurpation : jamais de fenêtre ni d'acceptation au nom du salarié ; un
//   bandeau signale l'absence de consentement.
// - Les écrans staff (/app/aon|drh|admin) ne sont pas concernés : un interne
//   qui aurait aussi une fiche salarié n'est pas bloqué dans son espace.
// =============================================================================

const STAFF_PREFIXES = ["/app/aon", "/app/drh", "/app/admin"];

export function AonPrivacyGate({ children }: { children: ReactNode }) {
  const { t } = useTranslation();
  const { isImpersonating } = useAuth();
  const location = useLocation();
  const { data: salarie, isLoading: salarieLoading } = useMySalarie();
  const isStaffPath = STAFF_PREFIXES.some((p) => location.pathname.startsWith(p));
  const { data: legal, isLoading: legalLoading } = useLegalTextCurrent(
    "aon_privacy",
    Boolean(salarie) && !isStaffPath,
  );

  if (salarieLoading || (salarie && legalLoading && !isStaffPath)) {
    return (
      <div role="status" className="grid min-h-screen place-items-center bg-bg">
        <div
          aria-hidden="true"
          className="size-8 animate-spin rounded-full border-2 border-brand border-t-transparent"
        />
        <span className="sr-only">{t("common.loading")}</span>
      </div>
    );
  }

  const needsConsent =
    Boolean(salarie) &&
    !isStaffPath &&
    legal != null &&
    aonPrivacyConsentNeeded(salarie?.aon_privacy_version, legal.required_version);

  if (!needsConsent) return <>{children}</>;

  if (isImpersonating) {
    return <ImpersonationConsentNotice>{children}</ImpersonationConsentNotice>;
  }

  return <PrivacyConsentScreen legal={legal!} />;
}

// -----------------------------------------------------------------------------
// Bandeau d'information en impersonation : aucune acceptation possible au nom
// du salarié, l'espace reste consultable par l'admin.
// -----------------------------------------------------------------------------
function ImpersonationConsentNotice({ children }: { children: ReactNode }) {
  const { t } = useTranslation();
  return (
    <>
      <div className="flex items-center gap-2 border-b border-warning/30 bg-warning/10 px-4 py-2 text-sm text-ink">
        <ShieldCheck className="size-4 shrink-0 text-warning" />
        {t("legal.privacy.impersonation_banner")}
      </div>
      {children}
    </>
  );
}

// -----------------------------------------------------------------------------
// Écran bloquant : l'espace salarié n'est pas monté derrière la fenêtre, donc
// aucune donnée métier n'est chargée avant l'acceptation.
// -----------------------------------------------------------------------------
function PrivacyConsentScreen({ legal }: { legal: LegalTextCurrent }) {
  const { t, i18n } = useTranslation();
  const { user, signOut } = useAuth();
  const { data: salarie } = useMySalarie();
  const accept = useAcceptAonPrivacy();
  const [checked, setChecked] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const text = pickLegalText(legal, i18n.language);
  const updated =
    salarie?.aon_privacy_version != null && salarie.aon_privacy_version < legal.required_version;

  async function confirm() {
    setError(null);
    try {
      await accept.mutateAsync(legal.version);
    } catch (e) {
      setError(e instanceof Error ? e.message : t("legal.privacy.error"));
    }
  }

  return (
    <div className="grid min-h-screen place-items-center bg-bg-subtle p-4">
      <Dialog
        open
        blocking
        onClose={() => undefined}
        size="lg"
        title={t("legal.privacy.title")}
        description={updated ? t("legal.privacy.updated") : undefined}
        footer={
          <div className="flex w-full flex-wrap items-center justify-between gap-3">
            <button
              type="button"
              className="focus-ring text-sm text-ink-muted hover:text-ink hover:underline"
              onClick={() => void signOut()}
            >
              {t("legal.privacy.logout")}
            </button>
            <Button
              onClick={() => void confirm()}
              disabled={!checked || accept.isPending}
              loading={accept.isPending}
            >
              {t("legal.privacy.enter")}
            </Button>
          </div>
        }
      >
        <div className="space-y-4" role="document" aria-label={t("legal.privacy.title")}>
          <LegalDocumentLinks />
          <LegalTextBody text={text} className="space-y-3 text-sm leading-relaxed text-ink" />

          <div className="rounded-xl border border-line bg-bg-subtle p-3">
            <IaMention />
          </div>

          <label className="flex cursor-pointer items-start gap-3 rounded-xl border border-line p-3 text-sm font-medium text-ink">
            <input
              type="checkbox"
              className="mt-0.5 size-4 shrink-0 accent-brand"
              checked={checked}
              onChange={(e) => setChecked(e.target.checked)}
            />
            {t("legal.privacy.accept")}
          </label>

          {error && (
            <p role="alert" className="text-sm text-danger">
              {error}
            </p>
          )}
          {user == null && <p className="text-sm text-danger">{t("legal.privacy.error")}</p>}
        </div>
      </Dialog>
    </div>
  );
}
